// Snippets typed out on the code screen of the "Skills" tab, in display order.
// Keep each one within 24 lines of ~68 characters so it fits without scrolling.

const vueSharedComposable = `
type Composable = (...args: any[]) => any;

/** One instance shared by every caller, disposed with the last. */
export function createShared<T extends Composable>(setup: T): T {
    let subscribers = 0;
    let scope: EffectScope | undefined;
    let state: ReturnType<T> | undefined;

    const release = () => {
        if (--subscribers > 0) return;
        scope?.stop(); // disposes every watcher & computed inside
        scope = state = undefined;
    };

    return ((...args: Parameters<T>) => {
        if (!scope) {
            scope = effectScope(true); // detached from the caller
            state = scope.run(() => setup(...args));
        }
        subscribers++;
        if (getCurrentScope()) onScopeDispose(release);
        return state;
    }) as T;
}`;

const videoInference = `
class FrameStream:
    """Decodes on a worker thread, infers on the GPU in batches."""

    def __init__(self, source: str, model: nn.Module, batch_size=8):
        self.capture = cv2.VideoCapture(source)
        self.model = model.eval().half().cuda()
        self.queue = Queue(maxsize=batch_size * 4)  # back-pressure
        self.batch_size = batch_size
        Thread(target=self._decode, daemon=True).start()

    def _decode(self) -> None:
        while (frame := self.capture.read()[1]) is not None:
            self.queue.put(frame[..., ::-1])  # BGR -> RGB
        self.capture.release()
        self.queue.put(None)  # end-of-stream sentinel

    @torch.inference_mode()
    def __iter__(self) -> Iterator[tuple[np.ndarray, Tensor]]:
        frames = iter(self.queue.get, None)
        while batch := list(islice(frames, self.batch_size)):
            x = torch.from_numpy(np.stack(batch)).pin_memory()
            x = x.cuda(non_blocking=True).permute(0, 3, 1, 2)
            yield from zip(batch, self.model(x.half() / 255))`;

const laravelMigration = `
return new class extends Migration
{
    public function up(): void
    {
        Schema::create('files', function (Blueprint $table) {
            $table->ulid('id')->primary();
            $table->foreignIdFor(User::class)
                ->constrained()
                ->cascadeOnDelete();
            $table->string('path')->unique();
            $table->string('mime_type', 127);
            $table->unsignedBigInteger('size');
            $table->char('sha256', 64)->index(); // dedupe uploads
            $table->timestamps();

            $table->index(['user_id', 'created_at']);
        });
    }

    public function down(): void
    {
        Schema::dropIfExists('files');
    }
};`;

const candleChart = `
// Draws visible candles only, batched into one path per trend.
function drawCandles(ctx, candles, { x, y, start, end, step }) {
    const paths = { up: new Path2D(), down: new Path2D() };
    const half = Math.max(1, Math.floor(step * 0.35));
    const stop = lowerBound(candles, end);

    for (let i = lowerBound(candles, start); i < stop; i++) {
        const { time, open, high, low, close } = candles[i];
        const path = close >= open ? paths.up : paths.down;
        const cx = Math.round(x(time)) + 0.5; // crisp 1px wick
        const top = y(Math.max(open, close));
        const bottom = y(Math.min(open, close));

        path.moveTo(cx, y(high));
        path.lineTo(cx, y(low));
        path.rect(cx - half, top, half * 2, bottom - top);
    }

    for (const [trend, path] of Object.entries(paths)) {
        ctx.fillStyle = ctx.strokeStyle = TREND_COLORS[trend];
        ctx.fill(path);
        ctx.stroke(path);
    }
}`;

const laravelController = `
final class SessionController extends Controller
{
    public function register(RegisterRequest $request): JsonResponse
    {
        $user = User::create($request->validated());

        return $this->issueToken($user)->setStatusCode(201);
    }

    public function login(LoginRequest $request): JsonResponse
    {
        $request->authenticate(); // throttled; throws on failure

        return $this->issueToken($request->user());
    }

    private function issueToken(User $user): JsonResponse
    {
        return UserResource::make($user)->additional([
            'token' => $user->createToken('api')->plainTextToken,
        ])->response();
    }
}`;

// `path` names the editor tab and breadcrumbs; its extension picks the language.
export const codeSnippets = [
    { path: 'src/composables/createShared.ts', code: vueSharedComposable },
    { path: 'database/migrations/create_files_table.php', code: laravelMigration },
    { path: 'vision/frame_stream.py', code: videoInference },
    { path: 'app/Http/Controllers/SessionController.php', code: laravelController },
    { path: 'src/charts/candles.js', code: candleChart },
].map((snippet) => ({ ...snippet, code: snippet.code.trimStart() })); // drop the literals' leading newline
